// ============================================================================
// RegionalProvider — moneda e idioma (oct-2026).
//
// - Moneda: una por instalación (backend). Se fija en lib/moneda para que
//   formatMoney() la use en toda la app. Si al cargar resulta distinta a la
//   que se pintó (caché vieja o primer arranque en USD), el árbol se vuelve a
//   montar UNA vez con la moneda correcta. Con COP no pasa nada de esto.
// - Idioma: cada usuario elige entre los habilitados en Features. Se guarda
//   en el backend (usuarios.idioma) y en este navegador (para el login).
//
// Valor por defecto del contexto = COP + español: un componente fuera del
// provider ve exactamente lo de siempre.
// ============================================================================
import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useUser } from './user-context';
import { getRegional, setMiIdioma, type EstadoRegional } from '../../api/regional';
import { getMonedaActual, setMonedaActual, esMonedaConfig, type MonedaConfig } from '../../lib/moneda';
import { IDIOMA_DEFECTO, esCodigoIdioma, type CodigoIdioma, type Clave } from '../../i18n/idiomas';
import { traducir, type VarsTraduccion } from '../../i18n/traducir';
import { getIdiomaActual, setIdiomaActual } from '../../i18n/actual';
import { textoDe, type Textos } from '../../i18n/textos';

export interface RegionalCtx {
  moneda: MonedaConfig;
  /** true con COP: FED, Bold y Mensajeros Urbanos conectados. */
  operaColombia: boolean;
  idioma: CodigoIdioma;
  idiomasHabilitados: CodigoIdioma[];
  /** true cuando ya respondió el backend. */
  cargado: boolean;
  t: (clave: Clave, vars?: VarsTraduccion) => string;
  cambiarIdioma: (i: CodigoIdioma) => Promise<void>;
  /** Aplica una respuesta de /api/regional (lo usa Features tras guardar). */
  aplicar: (e: EstadoRegional) => void;
}

const DEFECTO: RegionalCtx = {
  moneda: getMonedaActual(),
  operaColombia: getMonedaActual().operaColombia,
  idioma: IDIOMA_DEFECTO,
  idiomasHabilitados: [IDIOMA_DEFECTO],
  cargado: false,
  t: (clave, vars) => traducir(IDIOMA_DEFECTO, clave, vars),
  cambiarIdioma: async () => {},
  aplicar: () => {},
};

const RegionalContext = createContext<RegionalCtx>(DEFECTO);

export function RegionalProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [moneda, setMoneda] = useState<MonedaConfig>(() => getMonedaActual());
  const [idioma, setIdioma] = useState<CodigoIdioma>(() => getIdiomaActual());
  const [habilitados, setHabilitados] = useState<CodigoIdioma[]>([IDIOMA_DEFECTO]);
  const [cargado, setCargado] = useState(false);

  const aplicar = useCallback((e: EstadoRegional) => {
    if (esMonedaConfig(e.moneda)) {
      setMonedaActual(e.moneda);          // primero el módulo, después el render
      setMoneda(e.moneda);
    }
    const hab = (e.idiomas_habilitados || []).filter(esCodigoIdioma);
    setHabilitados(hab.length ? hab : [IDIOMA_DEFECTO]);
    const mio = esCodigoIdioma(e.mi_idioma) ? e.mi_idioma : IDIOMA_DEFECTO;
    setIdiomaActual(mio);               // primero el módulo (helpers), después el render
    setIdioma(mio);
    setCargado(true);
  }, []);

  useEffect(() => {
    if (!user) { setCargado(false); return; }
    let vivo = true;
    getRegional()
      .then(e => { if (vivo) aplicar(e); })
      .catch(() => { /* sin respuesta: queda lo de siempre (COP + idioma guardado) */ });
    return () => { vivo = false; };
  }, [user?.id, aplicar]);

  const cambiarIdioma = useCallback(async (i: CodigoIdioma) => {
    const e = await setMiIdioma(i);
    aplicar(e);
  }, [aplicar]);

  const valor = useMemo<RegionalCtx>(() => ({
    moneda,
    operaColombia: moneda.operaColombia,
    idioma,
    idiomasHabilitados: habilitados,
    cargado,
    t: (clave, vars) => traducir(idioma, clave, vars),
    cambiarIdioma,
    aplicar,
  }), [moneda, idioma, habilitados, cargado, cambiarIdioma, aplicar]);

  // key = moneda + idioma: si cambia cualquiera, el árbol se monta de nuevo y
  // todo (formatMoney, textoDe en helpers) se repinta con el valor nuevo. Con
  // COP + español la key no cambia nunca.
  return (
    <RegionalContext.Provider value={valor}>
      <Fragment key={`${moneda.codigo}|${idioma}`}>{children}</Fragment>
    </RegionalContext.Provider>
  );
}

export function useRegional(): RegionalCtx {
  return useContext(RegionalContext);
}

/** Atajo para componentes que solo traducen. */
export function useI18n() {
  const { t, idioma, idiomasHabilitados, cambiarIdioma } = useContext(RegionalContext);
  return { t, idioma, idiomasHabilitados, cambiarIdioma };
}

/**
 * Textos de un módulo (ver src/i18n/textos.ts). Devuelve t(clave, vars) en el
 * idioma del usuario, con español como respaldo.
 */
export function useTextos<K extends string>(m: Textos<K>) {
  const { idioma } = useContext(RegionalContext);
  return useCallback(
    (clave: K, vars?: VarsTraduccion) => textoDe(m, clave, vars, idioma),
    [m, idioma],
  );
}
